import React, {
  type PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import { PermissionsRequired } from "library";
import { Page } from "@/components/Page";
import { Alert } from "@/components/Alert";
import MarketGroup from "./MarketGroup";
import { toggleSelection } from "../Betslip/betslipSlice";
import type { ToggleSelectionPayload } from "hooks/firestore/betting/types";
import useDisableNativeScrollRestoration from "hooks/useDisableNativeScrollRestoration";
import { matchPageVisit } from "./Actions";
import { Helmet } from "react-helmet";
import { type RouteComponentProps, useLocation } from "@gatsbyjs/reach-router";
import {
  useAnnouncementCards,
  useDispatch,
  useHasPermission,
  useSameGameMulti,
  useSelections,
} from "hooks";
import { BettingTabs } from "types/BetTypes";
import {
  type BettingEventWithMarkets,
  isVisibleOnTab,
  useBettingEventWithMarkets,
} from "hooks/firestore/v2/betting/useEvent";
import useRoute from "hooks/useRoute";
import { useDebounce } from "usehooks-ts";
import { marketSearch } from "utilities/sharedBettingUtilities";
import { useGroupedMarkets } from "hooks/firestore/betting/useGroupedMarkets";
import { format, formatDistance } from "date-fns";
import { CampaignPromotions } from "../CampaignPromotions";
import { AnnouncementCards } from "components/AnnouncementCards";
import { titleCase } from "title-case";
import { EventBlocked } from "sections/Betting/components/EventBlocked";
import OutcomeIcon from "sections/Betting/Event/OutcomeIcon";
import { ComplexBetBuilder } from "sections/Betting/components";
import { Selections } from "../components/ComplexBetBuilder/components/SameEventMulti/Selections";
import { MarketsFilter } from "./MarketsFilter";
import { Tab, Tabs, TabsList } from "components/Tabs";
import { Scoreboard } from "./Scoreboard";
import { StatusWithStream } from "../Betslip/components/StatusWithStream";
import { PageMessage } from "components/PageMessage";
import { ReactComponent as EmptyIcon } from "sections/Share/assets/empty.svg";
import { ReactComponent as ExpiredIcon } from "sections/Share/assets/expired.svg";
import { NextBettingButtons } from "components/layout/NextBettingButtons";
import { Button } from "components/Button";
import { usePreferences } from "hooks/firestore/v2/user/usePreferences";
import type { Titles } from "hooks/graphql/useTitles";
import { TabPromoIcon } from "components/PromoIcon/TabPromoIcon";
import { useEventEntries } from "hooks/firestore/betting/useEntries";
import { MyBets } from "./MyBets";
import type { Entries } from "sections/Entries/types";
import { MatchPageSkeleton } from "./MatchPageSkeleton";

import * as styles from "./MatchPage.module.scss";

type MatchPageRoute = {
  id: string;
  tab: marketFilterTypeProps | "my-bets";
};

type MatchPageProps = {
  match?: BettingEventWithMarkets;
  loading: boolean;
  entriesLoading: boolean;
  titles: Record<any, any>;
  route: MatchPageRoute;
  setRoute: (arg: Partial<MatchPageRoute>) => void;
  path?: string;
  hasSGM: boolean;
  entries: Entries;
} & RouteComponentProps;

const MatchPageFacade = ({
  titles,
}: { titles: Titles } & RouteComponentProps) => {
  const [setRoute, route] = useRoute<{
    id: string;
    tab: marketFilterTypeProps;
    sports: string;
  }>("(/:sports)/betting/match/:id(/:tab)(/)", {
    tab: "all",
  });

  const { event: match, isLoading: loading } = useBettingEventWithMarkets(
    route.id,
    route.tab === "sgm" ? "sem" : "all",
  );

  const [entries, entriesLoading] = useEventEntries(
    route.id,
    String(match?.status),
  );
  const hasEntries = entries?.length > 0 || route.tab === "my-bets";

  const submitSGMBet = useHasPermission("submitSGMBet");
  const hasSGM = match?.attributes?.sgm && submitSGMBet;

  useEffect(() => {
    if (!submitSGMBet && route.tab === "sgm") {
      setRoute({ tab: "all" });
    }
  }, [submitSGMBet, route.tab]);

  if (!match && !loading) {
    return (
      <PageMessage
        image={<EmptyIcon />}
        title="Match not found"
        subTitle="This event is no longer active"
      >
        <NextBettingButtons />
      </PageMessage>
    );
  }

  const activeMarketCounts = match?.activeMarketCounts;
  const basePath = `/${route.sports ? `${route.sports}/` : ``}betting/match/${route.id}`;
  return (
    <MatchPage
      titles={titles}
      route={route}
      setRoute={setRoute}
      match={match}
      loading={loading}
      hasSGM={hasSGM}
      entries={entries}
      entriesLoading={entriesLoading}
    >
      <Tabs
        basePath={basePath}
        className={styles.tabGutter}
        isValueDerivedFromRouter
      >
        <TabsList>
          <Tab
            to={`/all`}
            badge={
              activeMarketCounts?.SingleOutcome
                ? String(activeMarketCounts.SingleOutcome)
                : null
            }
          >
            All Markets
            {match && (
              <TabPromoIcon
                tab={BettingTabs.SPORTS_SINGLES}
                campaigns={match?.promotionCampaigns}
                eventHasPromoBanner={match.showPromoBanner}
              />
            )}
          </Tab>
          {hasSGM && (
            <Tab
              to={`/sgm`}
              badge={
                activeMarketCounts?.SameEventMulti
                  ? String(activeMarketCounts.SameEventMulti)
                  : null
              }
            >
              Same Game Multi
              {match && (
                <TabPromoIcon
                  tab={BettingTabs.SPORTS_SEM}
                  campaigns={match?.promotionCampaigns}
                  eventHasPromoBanner={match.showPromoBanner}
                />
              )}
            </Tab>
          )}
          {hasEntries && (
            <Tab to={`/my-bets`} badge={String(entries.length)}>
              My Bets
            </Tab>
          )}
        </TabsList>
      </Tabs>
    </MatchPage>
  );
};

const MatchPage = ({
  match,
  loading,
  titles,
  route,
  setRoute,
  children,
  entries,
  entriesLoading,
}: PropsWithChildren<MatchPageProps>) => {
  const {
    SGMSelections,
    toggleSgmSelection,
    errorMessage,
    errorMessageToReplaceFooter,
    removeAllSgmSelections,
    removeSgmSelection,
    rollBackSelectionChange,
    setErrorMessage,
    setLatestValidSelections,
    latestValidSelections,
    errorMessageShouldAllowClear,
  } = useSameGameMulti();

  const cards = useAnnouncementCards();

  const dispatch = useDispatch();
  useDisableNativeScrollRestoration();
  const { preferences: userPreferences } = usePreferences();

  const {
    props: { selections: betSlipSelections },
  } = useSelections();

  const selections = route.tab === "all" ? betSlipSelections : SGMSelections;

  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [hasSetExpandedGroups, setHasSetExpandedGroups] = useState(false);

  const [searchFilter, setSearchFilter] = useState("");
  const debouncedSearchFilter = useDebounce(searchFilter, 500);

  const filteredMarkets = useMemo(() => {
    if (
      isVisibleOnTab(route.tab === "sgm" ? "sem" : "all", match?.mainMarket)
    ) {
      return marketSearch({
        markets: [match?.mainMarket, ...(match?.markets || [])],
        searchFilter: debouncedSearchFilter,
        mainMarketStatus: match?.mainMarket?.status,
      });
    } else {
      return marketSearch({
        markets: match?.markets,
        searchFilter: debouncedSearchFilter,
        mainMarketStatus: match?.mainMarket?.status,
      });
    }
  }, [debouncedSearchFilter, match?.markets, route.tab, match?.mainMarket]);

  const [filteredMarketsByGroup, groupsLoading] = useGroupedMarkets(
    !loading && match ? filteredMarkets : [],
    match?.sport,
    route.tab === "all",
  );

  useEffect(() => {
    setHasSetExpandedGroups(false);
  }, [route.tab]);

  useEffect(() => {
    if (debouncedSearchFilter.length === 0) {
      setExpandedGroups([filteredMarketsByGroup[0]?.name]);
    }
  }, [debouncedSearchFilter]);

  useEffect(() => {
    if (debouncedSearchFilter.length > 0) {
      setExpandedGroups(filteredMarketsByGroup.map((group) => group.name));
      setHasSetExpandedGroups(true);
    } else if (!hasSetExpandedGroups && filteredMarketsByGroup.length > 0) {
      setExpandedGroups([filteredMarketsByGroup[0]?.name]);
      setHasSetExpandedGroups(true);
    }
  }, [filteredMarketsByGroup, debouncedSearchFilter]);

  const toggleMarketGroup = (groupName) => {
    if (expandedGroups.includes(groupName)) {
      setExpandedGroups(
        expandedGroups.filter(
          (existingGroupName) => existingGroupName !== groupName,
        ),
      );
    } else {
      setExpandedGroups([...expandedGroups, groupName]);
    }
  };

  const location = useLocation();

  useEffect(() => {
    if (!loading && match != undefined) {
      dispatch(
        matchPageVisit({
          eventName: match?.eventName,
          sport: match?.sport,
        }),
      );
    }
  }, [loading]);

  const toggleSelectionWrapper = (pick: ToggleSelectionPayload) => {
    if (route.tab === "sgm") {
      toggleSgmSelection(pick);
    } else {
      dispatch(toggleSelection(match, pick, match.hub));
    }
  };

  const { seoTitle, seoDescription } = useMemo(() => {
    const eventName = match?.eventName ?? "";
    const tournamentName = match?.attributes?.tournamentName ?? "";
    const scheduledStartTime = match?.scheduledStartTime ?? new Date();
    const sport = match?.sport ?? "Sport";

    const formattedDate = format(scheduledStartTime, "dd/MM/yy");

    const seoTitle = `${titleCase(
      eventName,
    )} | ${tournamentName} - ${formattedDate}`;

    const seoDescription = `${titleCase(
      eventName,
    )} | ${tournamentName} - ${formattedDate} - ${titleCase(sport)}`;

    return { seoTitle, seoDescription };
  }, [match]);

  const mainMarketOutcomes = Object.values(match?.mainMarket?.outcomes || {});

  const homeOutcome = mainMarketOutcomes.find(({ type }) =>
    type?.endsWith("_HOME"),
  );
  const homeCompetitor = match?.competitors?.[homeOutcome?.competitorId];

  const awayOutcome = mainMarketOutcomes.find(({ type }) =>
    type?.endsWith("_AWAY"),
  );
  const awayCompetitor = match?.competitors?.[awayOutcome?.competitorId];
  const title = !loading ? titles[match?.sport?.toLowerCase()] : null;

  const permissionsRequired = ["viewBettingMarkets"];
  if (match?.hub === "esports") {
    permissionsRequired.push("viewEsportsMarkets");
  } else {
    permissionsRequired.push("viewSportsMarkets");
  }

  const SgmAvailableMessage = () =>
    match?.attributes?.semAvailableAtTs ? (
      <>
        Same Game Multi will be available in{" "}
        {formatDistance(match?.attributes?.semAvailableAtTs, new Date())}
        <br />(
        {formatDistance(
          match?.attributes?.semAvailableAtTs,
          match.scheduledStartTime,
        )}{" "}
        before scheduled start time)
      </>
    ) : (
      <>
        Same Game Multi will be available prior to the scheduled start time for
        this event
      </>
    );

  const renderMarketGroup = (
    { name: groupName, markets }: (typeof filteredMarketsByGroup)[number],
    i: number,
  ): React.ReactNode => (
    <MarketGroup
      key={`market-group-${groupName}`}
      name={groupName}
      markets={markets}
      event={match}
      toggleMarkets={() => toggleMarketGroup(groupName)}
      isOpen={expandedGroups.includes(groupName)}
      oddsFormat={userPreferences?.oddsFormat.toLowerCase()}
      selections={selections}
      toggleSelection={toggleSelectionWrapper}
      isSearchActive={debouncedSearchFilter !== ""}
      currentGroupIndex={i}
    />
  );

  return (
    <EventBlocked filters={match?.filters ?? []} loading={loading}>
      <PermissionsRequired
        permissionsRequired={permissionsRequired}
        skeleton={<MatchPageSkeleton withMargin />}
      >
        <>
          <Helmet>
            <link
              rel="canonical"
              href={`${location?.origin}${location?.pathname}`}
            />
            <title>{seoTitle}</title>
            <meta name="og:title" content={seoTitle} />
            <meta name="description" content={seoDescription} />
          </Helmet>
          <Page showSeparator={false} variant={"full"}>
            {!match && !loading && !groupsLoading ? (
              <Alert variant="warning">
                There are no active markets available for this match.
              </Alert>
            ) : (
              <div className={styles.match}>
                <Scoreboard
                  loading={loading}
                  title={{ ...title, name: match?.parentEventName }}
                  eventId={route.id}
                  home={homeCompetitor}
                  away={awayCompetitor}
                  renderTopRightSlot={() => (
                    <StatusWithStream
                      clampShape="rounded"
                      clampVariant="dark"
                      className={styles.statusAndStream}
                      status={match?.status}
                      scheduledStartTime={
                        match?.nextBetStop || match?.scheduledStartTime
                      }
                      title={match?.sport}
                      url={match?.streamUri ?? ""}
                      eventId={match?.eventId}
                      isPlaceholder={false}
                      shouldHideStream={["SETTLED", "RESULTED"].includes(
                        match?.status,
                      )}
                    />
                  )}
                />

                <AnnouncementCards cards={cards} noSpacing />
                {children}
                {route.tab !== "my-bets" &&
                  match?.promotionCampaigns?.length > 0 && (
                    <CampaignPromotions
                      campaigns={match?.promotionCampaigns}
                      tab={
                        route.tab === "all"
                          ? BettingTabs.SPORTS_SINGLES
                          : BettingTabs.SPORTS_SEM
                      }
                    />
                  )}
                {route.tab === "sgm" && !match?.isSGMReady ? (
                  <PageMessage
                    title="SGM Unavailable"
                    subTitle={<SgmAvailableMessage />}
                    image={<ExpiredIcon />}
                  >
                    <Button
                      onClick={() => setRoute({ tab: "all" })}
                      variant="secondary"
                    >
                      GO TO ALL MARKETS
                    </Button>
                  </PageMessage>
                ) : route.tab === "my-bets" ? (
                  <MyBets loading={entriesLoading} entries={entries || []} />
                ) : (
                  <MarketsFilter
                    value={searchFilter}
                    onValueChange={setSearchFilter}
                    onValueReset={() => setSearchFilter("")}
                    hasResults={filteredMarketsByGroup.length > 0}
                  >
                    {filteredMarketsByGroup.map(renderMarketGroup)}
                  </MarketsFilter>
                )}
                {route.tab === "sgm" && match?.isSGMReady && (
                  <ComplexBetBuilder
                    handleRemoveAllSelections={removeAllSgmSelections}
                    handleRemoveSelection={removeSgmSelection}
                    selections={selections}
                    errorMessage={errorMessage}
                    errorMessageToReplaceFooter={errorMessageToReplaceFooter}
                    errorMessageShouldAllowClear={errorMessageShouldAllowClear}
                    event={match}
                    markets={match?.markets}
                    rollBackSelectionChange={rollBackSelectionChange}
                    type={"sgm"}
                    setErrorMessage={setErrorMessage}
                    dependencies={[expandedGroups, searchFilter]}
                    setLatestValidSelections={setLatestValidSelections}
                    latestValidSelections={latestValidSelections}
                  >
                    <Selections
                      selections={selections}
                      markets={match?.markets}
                      handleRemoveSelection={removeSgmSelection}
                      renderSelectionName={(outcomeId: string) => {
                        const market = match.markets.find(
                          (m) => m.outcomes[outcomeId],
                        );

                        const outcome = market?.outcomes[outcomeId];

                        const competitor =
                          match.competitors?.[outcome?.competitorId];

                        return (
                          <div className={styles.sgmCompetitor}>
                            <OutcomeIcon
                              outcome={outcome}
                              competitor={competitor}
                              isSelected={false}
                              size={25}
                              className={styles.selectionIcon}
                            />
                            <div className={styles.selectionName}>
                              {titleCase(
                                outcome?.abbreviation ?? outcome?.name,
                              )}
                              <div className={styles.marketName}>
                                {market?.name}
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    />
                  </ComplexBetBuilder>
                )}
              </div>
            )}
          </Page>
        </>
      </PermissionsRequired>
    </EventBlocked>
  );
};

export default MatchPageFacade;
